<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore, useFunctionGetter } from 'dashboard/composables/store';
import easyOrdersAPI from 'dashboard/api/easyOrders';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const inboxes = useMapGetter('inboxes/getInboxes');
const integration = useFunctionGetter('integrations/getIntegration', 'easy_orders');

const logoUrl = '/dashboard/images/integrations/easy_orders.png';
const isLoading = ref(true);
const isWorking = ref(false);
const activeTab = ref('products');
const status = ref({});
const defaultInboxId = ref('');
const manualForm = reactive({ apiKey: '', storeId: '', webhookSecret: '' });
const orderLookupId = ref('');
const loadedOrder = ref(null);
const orderStatus = ref('processing');
const orderNoteText = ref('');
const stockForm = reactive({ sku: '', quantity: 0, productTaagerCode: '', variantTaagerCode: '' });

const resources = reactive({
  products: { page: 1, payload: null, loading: false },
  categories: { page: 1, payload: null, loading: false },
});

const tabs = computed(() => [
  { key: 'products', label: t('INTEGRATION_SETTINGS.EASY_ORDERS.TABS.PRODUCTS') },
  { key: 'categories', label: t('INTEGRATION_SETTINGS.EASY_ORDERS.TABS.CATEGORIES') },
  { key: 'order', label: t('INTEGRATION_SETTINGS.EASY_ORDERS.TABS.ORDER') },
  { key: 'webhooks', label: t('INTEGRATION_SETTINGS.EASY_ORDERS.TABS.WEBHOOKS') },
]);

const connected = computed(() => !!status.value.connected);
const configured = computed(() => !!status.value.configured);
const currentResource = computed(() => resources[activeTab.value]);
const currentItems = computed(() => extractItems(currentResource.value?.payload));
const inboxOptions = computed(() => inboxes.value || []);
const recentWebhooks = computed(() => status.value.recent_webhooks || []);

const extractItems = payload => {
  if (!payload) return [];
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.data?.data)) return payload.data.data;
  if (Array.isArray(payload.items)) return payload.items;
  return [];
};

const loadStatus = async () => {
  const { data } = await easyOrdersAPI.status();
  status.value = data;
  defaultInboxId.value = data.default_inbox_id || '';
  manualForm.storeId = data.store_id || manualForm.storeId;
};

const redirectIfServiceDisabled = () => {
  if (status.value.tenant_enabled !== false) return false;

  router.replace({ path: route.path.replace(/\/[^/]+\/?$/, '') });
  return true;
};

const loadResource = async type => {
  if (!connected.value || !['products', 'categories'].includes(type)) return;
  const resource = resources[type];
  resource.loading = true;
  try {
    const params = { page: resource.page, limit: 25 };
    const { data } = type === 'products' ? await easyOrdersAPI.products(params) : await easyOrdersAPI.categories(params);
    resource.payload = data;
  } catch (error) {
    useAlert(error.response?.data?.error || t('INTEGRATION_SETTINGS.EASY_ORDERS.API.ERROR'));
  } finally {
    resource.loading = false;
  }
};

const connectByInstallLink = async () => {
  isWorking.value = true;
  try {
    const { data } = await easyOrdersAPI.installUrl();
    window.location.href = data.url;
  } catch (error) {
    useAlert(error.response?.data?.error || t('INTEGRATION_SETTINGS.EASY_ORDERS.API.ERROR'));
  } finally {
    isWorking.value = false;
  }
};

const connectManually = async () => {
  isWorking.value = true;
  try {
    await easyOrdersAPI.connect({
      api_key: manualForm.apiKey,
      store_id: manualForm.storeId,
      webhook_secret: manualForm.webhookSecret,
      default_inbox_id: defaultInboxId.value,
    });
    manualForm.apiKey = '';
    await loadStatus();
    useAlert(t('INTEGRATION_SETTINGS.EASY_ORDERS.API.CONNECTED'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('INTEGRATION_SETTINGS.EASY_ORDERS.API.ERROR'));
  } finally {
    isWorking.value = false;
  }
};

const disconnect = async () => {
  if (!window.confirm(t('INTEGRATION_SETTINGS.EASY_ORDERS.DISCONNECT_CONFIRM'))) return;
  isWorking.value = true;
  try {
    await easyOrdersAPI.disconnect();
    await loadStatus();
    useAlert(t('INTEGRATION_SETTINGS.EASY_ORDERS.API.DISCONNECTED'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('INTEGRATION_SETTINGS.EASY_ORDERS.API.ERROR'));
  } finally {
    isWorking.value = false;
  }
};

const saveSettings = async () => {
  isWorking.value = true;
  try {
    await easyOrdersAPI.updateSettings({
      store_id: manualForm.storeId,
      webhook_secret: manualForm.webhookSecret,
      default_inbox_id: defaultInboxId.value,
    });
    await loadStatus();
    useAlert(t('INTEGRATION_SETTINGS.EASY_ORDERS.API.SAVED'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('INTEGRATION_SETTINGS.EASY_ORDERS.API.ERROR'));
  } finally {
    isWorking.value = false;
  }
};

const findOrder = async () => {
  isWorking.value = true;
  try {
    const { data } = await easyOrdersAPI.order(orderLookupId.value);
    loadedOrder.value = data;
    orderStatus.value = data.status || orderStatus.value;
  } catch (error) {
    useAlert(error.response?.data?.error || t('INTEGRATION_SETTINGS.EASY_ORDERS.API.ERROR'));
  } finally {
    isWorking.value = false;
  }
};

const updateOrderStatus = async () => {
  isWorking.value = true;
  try {
    await easyOrdersAPI.updateOrderStatus({ order_id: orderLookupId.value, status: orderStatus.value });
    await findOrder();
    useAlert(t('INTEGRATION_SETTINGS.EASY_ORDERS.API.ORDER_UPDATED'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('INTEGRATION_SETTINGS.EASY_ORDERS.API.ERROR'));
  } finally {
    isWorking.value = false;
  }
};

const addOrderNote = async () => {
  isWorking.value = true;
  try {
    await easyOrdersAPI.addOrderNote({
      order_id: orderLookupId.value,
      note: orderNoteText.value,
      store_id: manualForm.storeId || status.value.store_id,
      type: 'private',
    });
    orderNoteText.value = '';
    useAlert(t('INTEGRATION_SETTINGS.EASY_ORDERS.API.NOTE_ADDED'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('INTEGRATION_SETTINGS.EASY_ORDERS.API.ERROR'));
  } finally {
    isWorking.value = false;
  }
};

const updateStock = async () => {
  isWorking.value = true;
  try {
    await easyOrdersAPI.updateProductStock({
      sku: stockForm.sku,
      quantity: stockForm.quantity,
      product_taager_code: stockForm.productTaagerCode,
      variant_taager_code: stockForm.variantTaagerCode,
    });
    useAlert(t('INTEGRATION_SETTINGS.EASY_ORDERS.API.STOCK_UPDATED'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('INTEGRATION_SETTINGS.EASY_ORDERS.API.ERROR'));
  } finally {
    isWorking.value = false;
  }
};

const createConversation = async () => {
  if (!defaultInboxId.value || !loadedOrder.value) {
    useAlert(t('INTEGRATION_SETTINGS.EASY_ORDERS.SELECT_INBOX_FIRST'));
    return;
  }
  isWorking.value = true;
  try {
    await easyOrdersAPI.createConversation({ order: loadedOrder.value, inbox_id: defaultInboxId.value });
    useAlert(t('INTEGRATION_SETTINGS.EASY_ORDERS.API.CONVERSATION_CREATED'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('INTEGRATION_SETTINGS.EASY_ORDERS.API.ERROR'));
  } finally {
    isWorking.value = false;
  }
};

const setPage = async direction => {
  const resource = currentResource.value;
  const nextPage = resource.page + direction;
  if (nextPage < 1) return;
  resource.page = nextPage;
  await loadResource(activeTab.value);
};

const rowTitle = record => record.name || record.title || record.sku || record.id || '-';
const rowDetail = record =>
  record.price?.amount || record.price || record.sale_price || record.quantity || record.parent_id || record.slug || '-';

const initialize = async () => {
  try {
    await Promise.all([store.dispatch('integrations/get'), store.dispatch('inboxes/get')]);
    await loadStatus();
    if (redirectIfServiceDisabled()) return;
    if (route.query.connected) useAlert(t('INTEGRATION_SETTINGS.EASY_ORDERS.API.CONNECTED'));
    if (route.query.error) useAlert(route.query.error);
    if (connected.value) await loadResource(activeTab.value);
  } finally {
    isLoading.value = false;
  }
};

onMounted(initialize);
</script>

<template>
  <SettingsLayout :is-loading="isLoading">
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.EASY_ORDERS.HEADER')"
        :description="$t('INTEGRATION_SETTINGS.EASY_ORDERS.DESCRIPTION')"
        :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-5">
        <section class="flex flex-col gap-4 rounded-lg outline outline-1 outline-n-container bg-n-card p-5 md:flex-row md:items-center md:justify-between">
          <div class="flex gap-4">
            <img :src="logoUrl" class="h-14 w-14 rounded-lg border border-n-weak bg-n-alpha-2" alt="EasyOrders" />
            <div>
              <h3 class="text-heading-2 text-n-slate-12">{{ integration.name || 'EasyOrders' }}</h3>
              <p class="mt-1 text-body-main text-n-slate-11">{{ integration.description || $t('INTEGRATION_SETTINGS.EASY_ORDERS.DESCRIPTION') }}</p>
              <p v-if="connected" class="mt-2 text-sm text-n-teal-11">{{ $t('INTEGRATION_SETTINGS.EASY_ORDERS.CONNECTED_TO', { store: status.store_id || '-' }) }}</p>
              <p v-else-if="!configured" class="mt-2 text-sm text-n-ruby-11">{{ $t('INTEGRATION_SETTINGS.EASY_ORDERS.NOT_CONFIGURED') }}</p>
            </div>
          </div>
          <Button v-if="connected" faded ruby :is-loading="isWorking" :label="$t('INTEGRATION_SETTINGS.EASY_ORDERS.DISCONNECT')" @click="disconnect" />
          <Button v-else :disabled="!configured" :is-loading="isWorking" :label="$t('INTEGRATION_SETTINGS.EASY_ORDERS.CONNECT_INSTALL')" @click="connectByInstallLink" />
        </section>

        <section class="rounded-lg outline outline-1 outline-n-container bg-n-card p-4">
          <div class="grid gap-4 md:grid-cols-2">
            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              <span>{{ $t('INTEGRATION_SETTINGS.EASY_ORDERS.DEFAULT_INBOX') }}</span>
              <select v-model="defaultInboxId" class="h-10 rounded-md border border-n-weak bg-n-solid-2 px-3">
                <option value="">{{ $t('INTEGRATION_SETTINGS.EASY_ORDERS.NO_INBOX') }}</option>
                <option v-for="inbox in inboxOptions" :key="inbox.id" :value="inbox.id">{{ inbox.name }}</option>
              </select>
            </label>
            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              <span>{{ $t('INTEGRATION_SETTINGS.EASY_ORDERS.STORE_ID') }}</span>
              <Input v-model="manualForm.storeId" />
            </label>
            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              <span>{{ $t('INTEGRATION_SETTINGS.EASY_ORDERS.API_KEY') }}</span>
              <Input v-model="manualForm.apiKey" type="password" />
            </label>
            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              <span>{{ $t('INTEGRATION_SETTINGS.EASY_ORDERS.WEBHOOK_SECRET') }}</span>
              <Input v-model="manualForm.webhookSecret" type="password" />
            </label>
          </div>
          <div class="mt-4 flex flex-wrap gap-2">
            <Button :disabled="!manualForm.apiKey" :is-loading="isWorking" :label="$t('INTEGRATION_SETTINGS.EASY_ORDERS.CONNECT_MANUAL')" @click="connectManually" />
            <Button v-if="connected" faded slate :is-loading="isWorking" :label="$t('INTEGRATION_SETTINGS.EASY_ORDERS.SAVE_SETTINGS')" @click="saveSettings" />
          </div>
          <p v-if="status.orders_webhook_url" class="mt-3 break-all text-sm text-n-slate-11">
            {{ $t('INTEGRATION_SETTINGS.EASY_ORDERS.ORDERS_WEBHOOK') }}: {{ status.orders_webhook_url }}
          </p>
        </section>

        <section v-if="connected" class="rounded-lg outline outline-1 outline-n-container bg-n-card">
          <div class="flex flex-wrap gap-2 border-b border-n-weak p-3">
            <Button
              v-for="tab in tabs"
              :key="tab.key"
              size="sm"
              :variant="activeTab === tab.key ? 'solid' : 'faded'"
              color="slate"
              :label="tab.label"
              @click="activeTab = tab.key; loadResource(tab.key)"
            />
          </div>

          <div v-if="activeTab === 'products' || activeTab === 'categories'" class="overflow-x-auto p-4">
            <table class="min-w-full divide-y divide-n-weak text-sm">
              <thead>
                <tr class="text-left text-n-slate-11">
                  <th class="px-3 py-2">{{ $t('INTEGRATION_SETTINGS.EASY_ORDERS.TABLE.NAME') }}</th>
                  <th class="px-3 py-2">{{ $t('INTEGRATION_SETTINGS.EASY_ORDERS.TABLE.DETAIL') }}</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak text-n-slate-12">
                <tr v-if="currentResource.loading">
                  <td colspan="2" class="px-3 py-8 text-center text-n-slate-11">{{ $t('INTEGRATION_SETTINGS.EASY_ORDERS.LOADING') }}</td>
                </tr>
                <template v-else>
                  <tr v-for="record in currentItems" :key="record.id || record.sku">
                    <td class="px-3 py-3">{{ rowTitle(record) }}</td>
                    <td class="px-3 py-3 text-n-slate-11">{{ rowDetail(record) }}</td>
                  </tr>
                </template>
                <tr v-if="!currentResource.loading && !currentItems.length">
                  <td colspan="2" class="px-3 py-8 text-center text-n-slate-11">{{ $t('INTEGRATION_SETTINGS.EASY_ORDERS.EMPTY') }}</td>
                </tr>
              </tbody>
            </table>
            <div class="mt-4 flex items-center justify-between">
              <span class="text-sm text-n-slate-11">{{ $t('INTEGRATION_SETTINGS.EASY_ORDERS.PAGE', { page: currentResource.page }) }}</span>
              <div class="flex gap-2">
                <Button xs faded slate :disabled="currentResource.page <= 1 || currentResource.loading" :label="$t('INTEGRATION_SETTINGS.EASY_ORDERS.PREVIOUS')" @click="setPage(-1)" />
                <Button xs faded slate :disabled="!currentItems.length || currentResource.loading" :label="$t('INTEGRATION_SETTINGS.EASY_ORDERS.NEXT')" @click="setPage(1)" />
              </div>
            </div>
          </div>

          <div v-if="activeTab === 'order'" class="grid gap-5 p-4">
            <div class="flex flex-wrap gap-2">
              <Input v-model="orderLookupId" :placeholder="$t('INTEGRATION_SETTINGS.EASY_ORDERS.ORDER_ID')" class="max-w-md" @keyup.enter="findOrder" />
              <Button :disabled="!orderLookupId" :is-loading="isWorking" :label="$t('INTEGRATION_SETTINGS.EASY_ORDERS.FIND_ORDER')" @click="findOrder" />
            </div>
            <pre v-if="loadedOrder" class="max-h-80 overflow-auto rounded-md bg-n-alpha-2 p-3 text-xs text-n-slate-12">{{ JSON.stringify(loadedOrder, null, 2) }}</pre>
            <div v-if="loadedOrder" class="grid gap-3 md:grid-cols-2">
              <Input v-model="orderStatus" />
              <Button :is-loading="isWorking" :label="$t('INTEGRATION_SETTINGS.EASY_ORDERS.UPDATE_STATUS')" @click="updateOrderStatus" />
              <Input v-model="orderNoteText" :placeholder="$t('INTEGRATION_SETTINGS.EASY_ORDERS.ORDER_NOTE')" />
              <Button :disabled="!orderNoteText" faded slate :is-loading="isWorking" :label="$t('INTEGRATION_SETTINGS.EASY_ORDERS.ADD_NOTE')" @click="addOrderNote" />
              <Button faded blue :is-loading="isWorking" :label="$t('INTEGRATION_SETTINGS.EASY_ORDERS.CREATE_CONVERSATION')" @click="createConversation" />
            </div>
            <div class="grid gap-3 md:grid-cols-4">
              <Input v-model="stockForm.sku" :placeholder="$t('INTEGRATION_SETTINGS.EASY_ORDERS.SKU')" />
              <Input v-model="stockForm.productTaagerCode" :placeholder="$t('INTEGRATION_SETTINGS.EASY_ORDERS.PRODUCT_TAAGER_CODE')" />
              <Input v-model="stockForm.variantTaagerCode" :placeholder="$t('INTEGRATION_SETTINGS.EASY_ORDERS.VARIANT_TAAGER_CODE')" />
              <Input v-model="stockForm.quantity" type="number" :placeholder="$t('INTEGRATION_SETTINGS.EASY_ORDERS.QUANTITY')" />
              <Button faded slate :disabled="!stockForm.sku && !stockForm.variantTaagerCode" :is-loading="isWorking" :label="$t('INTEGRATION_SETTINGS.EASY_ORDERS.UPDATE_STOCK')" @click="updateStock" />
            </div>
          </div>

          <div v-if="activeTab === 'webhooks'" class="p-4">
            <table class="min-w-full divide-y divide-n-weak text-sm">
              <thead>
                <tr class="text-left text-n-slate-11">
                  <th class="px-3 py-2">{{ $t('INTEGRATION_SETTINGS.EASY_ORDERS.TABLE.TYPE') }}</th>
                  <th class="px-3 py-2">{{ $t('INTEGRATION_SETTINGS.EASY_ORDERS.TABLE.ORDER') }}</th>
                  <th class="px-3 py-2">{{ $t('INTEGRATION_SETTINGS.EASY_ORDERS.TABLE.STATUS') }}</th>
                  <th class="px-3 py-2">{{ $t('INTEGRATION_SETTINGS.EASY_ORDERS.TABLE.RECEIVED') }}</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak text-n-slate-12">
                <tr v-for="event in recentWebhooks" :key="`${event.type}-${event.id}-${event.received_at}`">
                  <td class="px-3 py-3">{{ event.type }}</td>
                  <td class="px-3 py-3">{{ event.id }}</td>
                  <td class="px-3 py-3">{{ event.status }}</td>
                  <td class="px-3 py-3">{{ event.received_at }}</td>
                </tr>
                <tr v-if="!recentWebhooks.length">
                  <td colspan="4" class="px-3 py-8 text-center text-n-slate-11">{{ $t('INTEGRATION_SETTINGS.EASY_ORDERS.EMPTY_WEBHOOKS') }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </template>
  </SettingsLayout>
</template>
